import { RfxContext } from "newui/features/Rfx/context/RfxContext";
import {
	doesSubmissionStatusMatch,
	hasInviteeSubmittedRfx,
} from "helpers/rfxHelper";
import { createContext, useContext, useMemo, useState } from "react";
import { RFX_SUBMISSION_STATUS } from "../Submission/constants";
import { Rfx } from "../types/rfx.types";
import { RfxSubmissionStatus } from "../Submission";
import { RfxSubmission } from "../Submission/types/submission.type";
import { isNullOrUndefined } from "newui/utils/objectUtil";

interface RfxSubmissionContextType {
	hasSubmitted: boolean;
	isOnboardingRFx: boolean;
	isStatusProgressOrAccepted: boolean;
	rfxData: Rfx | null;
	submission: RfxSubmission | null;
	setSubmissionData: (submission: RfxSubmission) => void;
	setSubmissionDate: (date: string) => void;
	setSubmissionStatus: (status: RfxSubmissionStatus) => void;
}

const RfxSubmissionContext = createContext<RfxSubmissionContextType>(null);

export const RfxSubmissionContextProvider = ({ children }) => {
	// we will use RfxContext
	const { isOnboardingRFx, rfxData } = useContext(RfxContext);

	// Submission Data
	const [submission, setSubmissionData] = useState<RfxSubmission | null>(null);

	// Computed Values
	const { hasSubmitted, isStatusProgressOrAccepted } = useMemo(() => {
		if (isNullOrUndefined(submission?.status)) {
			return { hasSubmitted: false, isStatusProgressOrAccepted: false };
		}

		const hasSubmitted = hasInviteeSubmittedRfx(submission?.status);
		const isStatusProgressOrAccepted = doesSubmissionStatusMatch(
			submission?.status,
			[RFX_SUBMISSION_STATUS.ACCEPTED, RFX_SUBMISSION_STATUS.INPROGRESS]
		);
		return { hasSubmitted, isStatusProgressOrAccepted };
	}, [submission]);

	const setSubmissionDate = (date: string) => {
		if (submission) {
			setSubmissionData((prev) =>
				prev ? { ...prev, submittedAt: date } : prev
			);
		}
	};

	const setSubmissionStatus = (status: RfxSubmissionStatus) => {
		if (submission) {
			setSubmissionData((prev) => (prev ? { ...prev, status } : prev));
		}
	};

	return (
		<RfxSubmissionContext.Provider
			value={{
				hasSubmitted,
				isOnboardingRFx,
				isStatusProgressOrAccepted,
				rfxData,
				submission,
				setSubmissionDate,
				setSubmissionStatus,
				setSubmissionData,
			}}
		>
			{children}
		</RfxSubmissionContext.Provider>
	);
};

export const useRfxSubmissionContext = (): RfxSubmissionContextType => {
	const context = useContext(RfxSubmissionContext);
	if (!context) {
		throw new Error(
			"useRfxSubmission must be used within RfxSubmissionContextProvider"
		);
	}
	return context;
};
